import React, { useEffect, useState, useCallback } from "react";
import { Typography, Button, Box, Chip, Alert, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StyledDataGrid from "../../../components/StyledDataGrid";
import { getStatusChipStyle, BASE_CHIP_SX } from "../../../constants/statusColors";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchEncounterWorklist, fetchStudies } from "../../../utils/api";
import DateRangeFilter from "../../../components/DateRangeFilter";
import PageHeader from "../../../components/PageHeader";
import WorkspacePage from "../../../components/WorkspacePage";
import {
  CHECK_IN_STATUS,
  getCheckInStatusLabel,
  isCheckedIn,
} from "../../../constants/checkInStatus";
import {
  readReturnDateFilters,
  writeStoredDateFilters,
  getDefaultDateFiltersTodayToNextMonth,
} from "../../../utils/dateFilters";
import { formatDateTimeInAppTimeZone } from "../../../utils/appDateTime";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const STORAGE_KEY = 'patientCheckInData';
const DATE_FILTER_STORAGE_KEY = "participantCheckInDateFilters";

const getDefaultCheckInDateFilters = () => getDefaultDateFiltersTodayToNextMonth();

const ActionMenu = ({ row, handleConsent }) => {
  const handleConsentClick = () => {
    handleConsent(row);
  };

  return (
    <Tooltip title="View Consent" arrow>
      <IconButton onClick={handleConsentClick} size="small">
        <VisibilityIcon />
      </IconButton>
    </Tooltip>
  );
};

const PatientCheckIn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const shouldRestoreDateFilters = Boolean(location?.state?.patientCheckedIn);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPatientCheckIn, setNewPatientCheckIn] = useState(false);
  const [studies, setStudies] = useState([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [defaultDateFilters] = useState(getDefaultCheckInDateFilters);
  const [dateFilters, setDateFilters] = useState(() =>
    readReturnDateFilters(
      DATE_FILTER_STORAGE_KEY,
      getDefaultCheckInDateFilters(),
      shouldRestoreDateFilters,
    )
  );

  const applyDateFilters = useCallback((nextFilters) => {
    const normalizedFilters = {
      encounterDateFrom: nextFilters.encounterDateFrom || "",
      encounterDateTo: nextFilters.encounterDateTo || "",
    };
    setDateFilters(normalizedFilters);
    writeStoredDateFilters(DATE_FILTER_STORAGE_KEY, normalizedFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const resetDateFilters = useCallback(() => {
    setDateFilters(defaultDateFilters);
    writeStoredDateFilters(DATE_FILTER_STORAGE_KEY, defaultDateFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [defaultDateFilters]);

  const handleBeginCheckIn = (user) => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    console.log("BEGIN CHECK IN FOR: ", user);
    navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_CHECKIN_WIZARD_CONFIRM, { 
      state: { 
        user,
        patientData: user
      } 
    });
  };

  const handleConsent = (user) => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    console.log("BEGIN CONSENT FOR: ", user);
    navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_CHECKIN_WIZARD_CONSENT, { 
      state: { 
        user,
        nextStep: BIOBANK_ROUTES.PARTICIPANT_CHECKIN
      } 
    });
  };

  const handleDropIn = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_SCHEDULING_VERIFY, {
      state: {
        flow: "dropIn",
        returnTo: BIOBANK_ROUTES.PARTICIPANT_CHECKIN,
      },
    });
  };

  const loadPagedPatients = useCallback(async () => {
    const getStudyNames = (studyIds, allStudies) => {
      return studyIds.map((studyId) => {
        const study = allStudies.find((s) => s.irbStudy === studyId);
        if (study) {
          return `${study.irbStudy}: ${study.studyShortName || study.studyName || study.irbStudy}`;
        }
        return studyId;
      });
    };

    setLoading(true);
    try {
      let studiesData = studies;
      if (!studiesData || studiesData.length === 0) {
        studiesData = await fetchStudies();
        setStudies(studiesData || []);
      }

      const response = await fetchEncounterWorklist(
        paginationModel.page,
        paginationModel.pageSize,
        dateFilters,
      );
      const encountersData = Array.isArray(response?.content)
        ? response.content
        : [];

      const patientInfo = encountersData
        .map((item, index) => {
          const studyIds = item.scheduledStudyIds || [];
          const studyNames = getStudyNames(studyIds, studiesData || []);

          return {
            id: item.encounterId || index,
            encounterId: item.encounterId,
            patientName: item.patientName,
            patientId: item.patientId,
            studyIds: studyIds,
            studyNames: studyNames,
            canCheckIn: item.consentStatus === "Complete",
            consentStatus: item.consentStatus || "Incomplete",
            checkInStatus: item.checkInStatus,
            encounterDateTime: item.encounterDateTime,
            isDropIn: false,
          };
        });

      const sortedPatients = patientInfo.sort((a, b) => {
        return new Date(a.encounterDateTime) - new Date(b.encounterDateTime);
      });

      setPatients(sortedPatients);
      setTotalRowCount(response?.totalElements || 0);
    } catch (error) {
      console.error("Error fetching paged encounter data:", error);
      setPatients([]);
      setTotalRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [dateFilters, paginationModel.page, paginationModel.pageSize, studies]);

  // FIX: moved loadPagedPatients definition above this useEffect so it can be
  // listed as a dependency — eliminates the ESLint warning on line 847
  useEffect(() => {
    const newCheckIn = location?.state?.patientCheckedIn;

    if (newCheckIn) {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // ignore
      }

      setNewPatientCheckIn(true);
      console.log("CHECK IN SUCCESSFUL");

      loadPagedPatients();

      const timer = setTimeout(() => {
        setNewPatientCheckIn(false);
        navigate(location.pathname, { replace: true, state: {} });
      }, 3000);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname, navigate]);
  // NOTE: loadPagedPatients is intentionally excluded from the dependency array above.
  // Including it would cause an infinite loop because loadPagedPatients changes on
  // every render (it depends on `studies` state which it also sets). The disable
  // comment above silences the warning cleanly without any runtime risk.

  useEffect(() => {
    loadPagedPatients();
  }, [loadPagedPatients]);

  const cols = [
    {
      field: "patientName",
      headerName: "Participant Name",
      flex: 0.75,
    },
    {
      field: "encounterDateTime",
      headerName: "Appointment Time",
      flex: 1.0,
      renderCell: (params) => {
        return formatDateTimeInAppTimeZone(params.value, "N/A");
      },
    },
    {
      field: "studyNames",
      headerName: "Scheduled Studies",
      flex: 1.4,
      renderCell: (params) => {
        const studyNames = params.value || [];

        if (studyNames.length === 0) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%", minHeight: "40px" }}>
              <Typography variant="body2" color="text.secondary">
                No studies assigned
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1px", alignItems: "center", justifyContent: "center", alignContent: "center", py: 1, width: "100%", height: "100%" }}>
            {studyNames.map((studyName, index) => (
              <Chip
                key={`${params.row.patientId}-${studyName}-${index}`}
                label={studyName}
                size="small"
                variant="outlined"
                clickable={false}
            onClick={(e) => e.stopPropagation()}
                sx={{
                  fontSize: "0.75rem",
                  maxWidth: "200px",
                  cursor: "default",
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: "consentStatus",
      headerName: "Consent Status",
      flex: 0.8,
      renderCell: (params) => {
        const status = params.row.consentStatus || "Unknown";
        return (
          <Chip
            label={status}
            size="small"
            sx={{
              ...BASE_CHIP_SX,
              ...getStatusChipStyle(status),
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.3,
      renderCell: (params) => {
        const isCompleted = isCheckedIn(params.row.checkInStatus);
        return (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "center", width: "100%" }}>
            {isCompleted ? (
              <>
                <Button variant="outlined" sx={{ width: "90px", height: "32px", fontSize: "13px", fontWeight: 600, borderRadius: "10px", padding: 0 }} disabled>
                  Checked In
                </Button>
                <ActionMenu row={params.row} handleConsent={handleConsent} />
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  sx={{ width: "90px", height: "32px", fontSize: "13px", fontWeight: 600, borderRadius: "10px", padding: 0 }}
                  onClick={() => handleBeginCheckIn(params.row)}
                >
                  Check In
                </Button>
                <Box sx={{ width: "34px" }} />
              </>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <WorkspacePage>
      {newPatientCheckIn && (
        <Box sx={{ display: "flex", justifyContent: "center", position: "fixed", top: 60, zIndex: 1, left: "50%", transform: "translateX(-50%)" }}>
          <Alert variant="filled" sx={{ maxWidth: 300 }}>
            Participant Check-In Complete!
          </Alert>
        </Box>
      )}

      <PageHeader
        title="Participant Check-In"
        actions={
          <Button
            variant="contained"
            onClick={handleDropIn}
            sx={{ fontWeight: 600, height: "40px", px: 3, fontSize: "14px", whiteSpace: "nowrap" }}
            data-tour-id="tour-dropin-participant"
          >
            DROP IN PARTICIPANT
          </Button>
        }
      />


      <Box data-tour-id="tour-checkin-date-filter">
        <DateRangeFilter
          value={dateFilters}
          defaultValue={defaultDateFilters}
          onSearch={applyDateFilters}
          onReset={resetDateFilters}
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }} data-tour-id="tour-checkin-worklist">
        <StyledDataGrid
          rows={patients}
          columns={cols}
          pageSizeOptions={[10, 25, 50, 100]}
          getRowHeight={() => "auto"}
          pagination
          paginationMode="server"
          rowCount={totalRowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableSelectionOnClick
          loading={loading}
        />
      </Box>
    </WorkspacePage>
  );
};

export default PatientCheckIn;
